// Row labels exactly as nukul_backend emits them (service/shrimp_size_table.go).
// They are the wire contract between the two repos — renaming one here without
// renaming it there silently drops the row's translation and unit.
export enum ShrimpSizeRow {
  Age = 'อายุกุ้ง',
  FeedBrand = 'ยี่ห้ออาหาร',
  FeedPerDay = 'อาหารต่อวัน',
  Size = 'ไซส์กุ้ง',
  WoundPercent = 'เปอร์เซ็นต์แผล',
  ADGPeriod = 'ADG ช่วง',
  ADGTotal = 'ADG รวม',
  FeedTotal = 'อาหารสะสม',
  Vitamin = 'วิตามิน',
}

// Each row shows its name and, where it has one, a unit rendered in a lighter
// weight beside it — "อาหารต่อวัน (กก.)". They are separate keys so the two can be
// styled apart without parsing a parenthesis out of the translated string.
export const ROW_TRANSLATION: Record<string, string> = {
  [ShrimpSizeRow.Age]: 'shrimpSize.table.age',
  [ShrimpSizeRow.FeedBrand]: 'shrimpSize.table.feedBrand',
  [ShrimpSizeRow.FeedPerDay]: 'shrimpSize.table.feedPerDay',
  [ShrimpSizeRow.Size]: 'shrimpSize.table.size',
  [ShrimpSizeRow.WoundPercent]: 'shrimpSize.table.woundPercent',
  [ShrimpSizeRow.ADGPeriod]: 'shrimpSize.table.adgPeriod',
  [ShrimpSizeRow.ADGTotal]: 'shrimpSize.table.adgTotal',
  [ShrimpSizeRow.FeedTotal]: 'shrimpSize.table.feedTotal',
  [ShrimpSizeRow.Vitamin]: 'shrimpSize.table.vitamin',
}

// NK-281: เปอร์เซ็นต์แผล is shown as "% แผล", which carries its own unit.
export const ROW_UNIT_TRANSLATION: Record<string, string> = {
  [ShrimpSizeRow.Age]: 'shrimpSize.table.age.unit',
  [ShrimpSizeRow.FeedPerDay]: 'shrimpSize.table.feedPerDay.unit',
  [ShrimpSizeRow.Size]: 'shrimpSize.table.size.unit',
  [ShrimpSizeRow.ADGPeriod]: 'shrimpSize.table.adgPeriod.unit',
  [ShrimpSizeRow.ADGTotal]: 'shrimpSize.table.adgTotal.unit',
  [ShrimpSizeRow.FeedTotal]: 'shrimpSize.table.feedTotal.unit',
}

export const TRANSLATION_KEYS = {
  HEADER: {
    POND: 'shrimpSize.table.pond',
    DATE: 'shrimpSize.table.date',
    EXPAND: 'shrimpSize.table.expand',
    COLLAPSE: 'shrimpSize.table.collapse',
  },
  BADGE: {
    CHILD_POND: 'shrimpSize.table.childPond',
  },
  MESSAGES: {
    NO_DATA: 'noData',
    LOAD_MORE: 'loadMore',
  },
} as const

export const DISPLAY_VALUES = {
  EMPTY: '-',
} as const

// NK-281: a collapsed pond shows only its age and size; ดูเพิ่ม opens the other
// seven rows, in the order the backend emits them.
export const COLLAPSED_ROWS: ReadonlySet<string> = new Set([ShrimpSizeRow.Age, ShrimpSizeRow.Size])

// The row tinted green while a pond is expanded, so the eye finds size among nine.
export const HIGHLIGHT_ROW = ShrimpSizeRow.Size

// วิตามิน is the one row whose value is free text rather than a number. It is held
// open at three lines whether or not today's value needs them — a row that changed
// height as the farm's vitamin list grew and shrank made the block jump — and still
// grows past that for a longer value instead of clipping it (NK-276).
export const TEXT_ROWS: ReadonlySet<string> = new Set([ShrimpSizeRow.Vitamin])

export const TEXT_ROW_MIN_LINES = 3
export const TEXT_ROW_LINE_HEIGHT = 1.25

// The backend pages a FARM-wide date axis (five dates a page), but each pond keeps
// only the dates it was measured on, so one page can leave a pond with a single
// column. Right after the first page the table keeps fetching until every pond has
// this many dates — bounded by AUTO_FILL_MAX_PAGES so a pond that was measured once
// cannot drag the farm's whole history in. Older dates past that load on demand.
export const MIN_VISIBLE_DATES = 5
export const AUTO_FILL_MAX_PAGES = 3

// Scrolling a pond's dates to within this many px of the oldest one loads the next
// page, like dragging past the end of the ผลเชื้อ table.
export const LOAD_MORE_SCROLL_THRESHOLD = 8
